import React, { useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Card } from '../components/Layout';
import ProgressButton from '../components/ProgressButton';
import styled from 'styled-components';
import bookingApi from '../service/bookingApi';
import { useNavigate } from 'react-router-dom';
import { ToastContext } from '../contexts/ToastProvider';
import charToEntity from '../utils/charUtils';

const Wrapper = styled.div`
  gap: 10px;
  justify-content: center;
  padding: 10px;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const ButtonCheckbox = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 8px;
  background-color: ${({ isChecked, theme }) =>
    isChecked ? theme.colors.primary : theme.colors.white};
  color: ${({ isChecked, theme }) =>
    isChecked ? theme.colors.white : theme.colors.primary};
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: ${({ isChecked, theme }) =>
    isChecked ? theme.colors.primary : theme.colors.background};
  }
`;

const QuestionNaire = ({ onNext }) => {
  const [answers, setAnswers] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const customerId = location.state?.customerId || null;
  const {toast} = useContext(ToastContext)
  if (!customerId) {
    return <p>Fejl: Mangler booking ID. Prøv at gå tilbage og bekræft bookingen igen.</p>;
  }
  // Kontraindikationer are already answered in the pre-booking screening (Step5)
  // and persisted to the clinic record via `healthInfo` (prepended to the Gecko
  // note in the backend), so the question is intentionally NOT repeated here.
  // Per Nanna's testing feedback 2026-06-20.
  const questions = [
    {
      title: 'Opmærksomhedspunkter',
      options: [
        'Ny diagnosticeret stofskiftesygdom',
        'Prædiabetes',
        'Sygdomme i mave eller tarm',
        'Forhøjet kolesterol',
        'Sygdom eller stærke smerter i led',
        'Ingen af ovenstående',
      ],
    },
    {
      title: 'Fejler du andet?',
      options: ['Ja', 'Nej'],
      description: true,
    },
    {
      title: 'Har du allergi over for medicin eller andet?',
      options: ['Ja', 'Nej'],
      description: true,
    },
    {
      title: 'Tager du lægeordineret medicin?',
      options: ['Ja', 'Nej'],
      description: true,
    },
    {
      title: 'Livsstil',
      options: [
        'Ryger',
        'Drikker mere end 10 genstande om ugen',
        'Spiser ofte fastfood',
        'Har stillesiddende arbejde',
        'Motionerer mindre end 2-3 gange om ugen',
        'Spiser snacks',
        'Har en "sød tand"',
        'Ingen af ovenstående',
      ],
    },
    {
      title: 'Graviditet',
      options: ['Ja', 'Nej'],
    },
    {
      title: 'Prævention',
      options: ['Ja', 'Nej'],
    },
    {
      title: 'Samtykke',  
      options: [
        'Jeg har forsøgt at tabe mig gennem kost og motion',
        'Jeg giver samtykke til at Diætisthuset må tilgå mine sundhedsoplysninger',
      ],
    },
    {
      title: 'Videregivelse af oplysninger',
      question: 'Skal vi informere din egen praktiserende læge om behandlingen hos os?',
      options: ['Ja', 'Nej'],
    },
  ];

  const handleToggle = (question, option) => {
    setAnswers((prev) => {
      const currentAnswers = prev[question] || [];
      const questionOptions = questions.find(q => q.title === question)?.options || [];

      if (questionOptions.length === 2 && questionOptions.includes('Ja') && questionOptions.includes('Nej')) {
        return { ...prev, [question]: [option] };
      }

      if (option === 'Ingen af ovenstående') {
        return currentAnswers.includes(option)
          ? { ...prev, [question]: [] }
          : { ...prev, [question]: [option] };
      }

      if (currentAnswers.includes('Ingen af ovenstående')) {
        return { ...prev, [question]: [option] };
      }

      return {
        ...prev,
        [question]: currentAnswers.includes(option)
          ? currentAnswers.filter((item) => item !== option)
          : [...currentAnswers, option],
      };
    });
  };

  const handleNext = async () => {
    for (let question of questions) {
      const hasAnswer = answers[question.title] && answers[question.title].length > 0;

      if (!hasAnswer) {
        toast.error(`Vær venlig at besvare spørgsmålet: "${question.title}"`)
        return;
      }
  
      if (question.description && answers[question.title].includes('Ja')) {
        const descriptionKey = `${question.title} Description`;
        if (!answers[descriptionKey] || answers[descriptionKey].trim().length === 0) {
          toast.error(`Vær venlig at indtaste en beskrivelse for spørgsmålet: "${question.title}"`);
          return;
        }
      }

      if (question.title === "Samtykke") {
        if (answers[question.title]?.length !== 2) {
          toast.error('Du skal acceptere begge punkter i "Samtykke" for at fortsætte.');
          return;
        }
      }
    }
  
    let outputString = `Spørgsmål:\n` + Object.entries(answers)
      .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
      .join('\n');
    
    let sanitizedOutputString = charToEntity(outputString)
  
    console.log("question naire: " + outputString);
    try {
      const response = await bookingApi.UpdateQuestionNaire(customerId, sanitizedOutputString);
      console.log(response);
    } catch (error) {
      console.error('Error update questionaire:', error);
      toast.error('Der opstod en fejl. Prøv venligst igen.');
    }
    
    navigate('/success-question');
  };
  
  return (
    <Container>
      <Card >
        <h1>Spørgeskema</h1>
        {questions.map((question, index) => (
          <Wrapper key={index}>
            <h3>{question.title}
              <br/> <br/>
              {question.question}
            </h3>
            
            {question.options.map((option, idx) => (
              <ButtonCheckbox
                key={idx}
                isChecked={answers[question.title]?.includes(option)}
                onClick={() => handleToggle(question.title, option)}
              >
                {option}
              </ButtonCheckbox>
            ))}
            {question.description && answers[question.title]?.includes('Ja') && (
              <textarea
                rows="4"
                placeholder="Beskriv venligst her..."
                onChange={(e) =>
                  setAnswers({ ...answers, [`${question.title} Description`]: e.target.value })
                }
              />
            )}
          </Wrapper>
        ))}
        <div>
          <ProgressButton text="Videre" onClick={handleNext} />
        </div>
      </Card>
    </Container>
  );
};

export default QuestionNaire;
